// Third Party
import isEqual from 'lodash/isEqual';

// Redux
import { ApiAction, AsyncResult, Result } from '../index';
import api from '../redux-api';

// Proposal Namespace
import * as ProposalNS from '../../modules/proposal/Proposal';

// Constants and types
import { PROPOSAL_BANNER_STYLES, PROPOSAL_ORIENTATION, PROPOSAL_PAPER_FORMAT } from '../../modules/proposal/utils/constants';
import { PERFORMANCE_CHART, ComparisonToolbarKeys } from '../../utils/constants';
import { ProposalTemplateOptions, ProposalV2State } from './types/proposal-v2';
import Dictionary from '@/types/dictionary';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import ComparisonItem from '@/types/comparison-item';
import CorrelationMetric from '@/types/correlation-metric';
import RiskProfileItem from '@/types/risk-profile-item';
import Nullable from '@/types/nullable';
import CancellablePromise from '@/types/cancellable-promise';
import Locale from '@/types/locale';
import ProposalTextAreaData from '@/types/proposal-text-area-data';
import LocalizedString from '@/types/localized-string';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import ProposalPdfMetadata from '@/types/proposal-pdf-metadata';
import { ApiResponse } from '@/utils/api';

// Actions
import {
  setChartOptions,
  setComment,
  setCalendarYearPeriod,
  setRiskPeriod,
  setFundRiskPeriod,
  setPortfolioRiskPeriod,
  setGroupRiskPeriod,
  setFundCalendarYearPeriod,
  setPortfolioCalendarYearPeriod,
  setGroupCalendarYearPeriod,
} from './comparison';
import { logDownloadProposal } from '../../services/usage-log';

// Utils
import localStorageUtils from '../../utils/local-storage';
import { LOCALE } from '../../utils/i18n';
import { getCurrentStateComparison } from '../../utils/comparison';
import { overrideItemColors } from '../../modules/proposal/utils/proposal';
import { getMissingHoldingsProxyLog } from '@/utils/custom-proxies';

const INITIAL_PROPOSAL_OPTIONS = {
  locale: LOCALE.EN_CA,
  hiddenLayouts: [],
  divisions: [],
  sections: [],
  views: {} as Dictionary<string>,
  theme: '',
  styleOptions: {
    banners: {
      ...PROPOSAL_BANNER_STYLES[0],
      color: '#fff',
    },
    orientation: PROPOSAL_ORIENTATION.PORTRAIT,
    paperFormat: PROPOSAL_PAPER_FORMAT.LETTER,
  },
  comments: {},
} as ProposalTemplateOptions;

// Initial State
const INITIAL_STATE: ProposalV2State = {
  items: [],
  templates: [],
  toolbarOptions: {} as Dictionary<ProposalToolbarOption>,
  proposalTemplateOptions: INITIAL_PROPOSAL_OPTIONS,
  gifItems: [],
};

// Actions
enum ProposalV2ActionTypes {
  CreateOmniPresentation = 'connect/proposal-v2/create-omni-presentation',
  SetItems = 'connect/proposal-v2/set-proposal-items',
  SetItemsCorrelation = 'connect/proposal-v2/set-proposal-items-correlation',
  SetGifItems = 'connect/proposal-v2/set-gif-items',
  SetProposalItemFlags = 'connect/proposal-v2/set-proposal-item-flags',
  ToggleGifHideBaseScenario = 'connect/proposal-v2/toggle-gif-hide-base-scenario',
  SetTemplate = 'connect/proposal-v2/set-template',
  SetProposalTemplateOptions = 'connect/proposal-v2/set-proposal-template-options',
  SetRiskProfile = 'connect/proposal-v2/set-risk-profile',
  SetToolbarOption = 'connect/proposal-v2/set-toolbar-option',
  DownloadPDF = 'connect/proposal-v2/download-pdf',
  CancelPDFDownload = 'connect/proposal-v2/cancel-pdf-download',
  SetProposalContact = 'connect/proposal-v2/set-contact',
  SetProposalDate = 'connect/proposal-v2/set-date',
  ResetProposal = 'connect/proposal-v2/reset-proposal',
  GetProposalTemplates = 'connect/proposal-v2/get-proposal-templates',
  SetProposalTemplates = 'connect/proposal-v2/set-proposal-templates',
  SetDefaultTemplate = 'connect/proposal-v2/set-default-template',
  ChangeProposalItemsOrder = 'connect/proposal-v2/change-proposal-items-order',
  ChangeProposalContentOrder = 'connect/proposal-v2/change-proposal-content-order',
}

type ResetProposalAction = {
  type: ProposalV2ActionTypes.ResetProposal;
};

type SetItemsAction = {
  type: ProposalV2ActionTypes.SetItems;
  items: ComparisonItem[];
};

type SetItemsCorrelationAction = {
  type: ProposalV2ActionTypes.SetItemsCorrelation;
  itemsCorrelation: CorrelationMetric;
};

type SetGifItemsAction = {
  type: ProposalV2ActionTypes.SetGifItems;
  items: BMOGIFItem[];
};

type SetProposalItemFlagsAction = {
  type: ProposalV2ActionTypes.SetProposalItemFlags;
  itemId: string;
  flags: string[];
};

type ToggleGifHideBaseScenarioAction = {
  type: ProposalV2ActionTypes.ToggleGifHideBaseScenario;
  value: boolean;
};

type SetTemplateAction = {
  type: ProposalV2ActionTypes.SetTemplate;
  template: ProposalNS.ProposalTemplateV2;
};

type SetRiskProfileAction = {
  type: ProposalV2ActionTypes.SetRiskProfile;
  riskProfile: RiskProfileItem;
};

type SetToolbarOptionAction = {
  type: ProposalV2ActionTypes.SetToolbarOption;
  toolbarKey: string;
  toolbarOption: ProposalToolbarOption;
};

type SetProposalContactAction = {
  type: ProposalV2ActionTypes.SetProposalContact;
  contact: ProposalNS.ProposalContact;
};

type SetProposalDateAction = {
  type: ProposalV2ActionTypes.SetProposalDate;
  date: Date;
};

type SetProposalTemplateOptionsAction = {
  type: ProposalV2ActionTypes.SetProposalTemplateOptions;
  proposalTemplateOptions: ProposalTemplateOptions;
};

type GetProposalTemplatesAction = ApiAction & {
  type: ProposalV2ActionTypes.GetProposalTemplates;
};

type SetProposalTemplatesAction = {
  type: ProposalV2ActionTypes.SetProposalTemplates;
  templates: ProposalNS.ProposalTemplateV2[];
};

type SetDefaultTemplateAction = {
  type: ProposalV2ActionTypes.SetDefaultTemplate;
  defaultTemplate: string;
};

type DownloadPDFAction = {
  type: ProposalV2ActionTypes.DownloadPDF;
  request?: Nullable<CancellablePromise<Blob>>;
};

type CancelPDFDownloadAction = {
  type: ProposalV2ActionTypes.CancelPDFDownload;
};

type ChangeProposalItemsOrderAction = {
  type: ProposalV2ActionTypes.ChangeProposalItemsOrder;
  itemIds: string[];
};

type ChangeProposalContentOrderAction = {
  type: ProposalV2ActionTypes.ChangeProposalContentOrder;
  sections: string[];
};

type ProposalV2Action =
  | ResetProposalAction
  | SetItemsAction
  | SetItemsCorrelationAction
  | SetGifItemsAction
  | SetProposalItemFlagsAction
  | ToggleGifHideBaseScenarioAction
  | SetTemplateAction
  | SetRiskProfileAction
  | SetToolbarOptionAction
  | SetProposalContactAction
  | SetProposalDateAction
  | SetProposalTemplateOptionsAction
  | GetProposalTemplatesAction
  | SetDefaultTemplateAction
  | SetProposalTemplatesAction
  | DownloadPDFAction
  | CancelPDFDownloadAction
  | ChangeProposalItemsOrderAction
  | ChangeProposalContentOrderAction;

// Reducer
export default function reducer(state: ProposalV2State = INITIAL_STATE, action: ProposalV2Action): ProposalV2State {
  switch (action.type) {
    case ProposalV2ActionTypes.ResetProposal: {
      return {
        ...INITIAL_STATE,
        templates: state.templates,
        selectedTemplate: state.selectedTemplate,
        proposalTemplateOptions: state.proposalTemplateOptions,
        defaultTemplate: state.defaultTemplate,
      };
    }
    case ProposalV2ActionTypes.SetItems: {
      return {
        ...state,
        items: action.items,
      };
    }
    case ProposalV2ActionTypes.SetItemsCorrelation: {
      return {
        ...state,
        itemsCorrelation: action.itemsCorrelation,
      };
    }
    case ProposalV2ActionTypes.SetGifItems: {
      return {
        ...state,
        gifItems: action.items,
      };
    }
    case ProposalV2ActionTypes.SetProposalItemFlags: {
      const itemToChangeIndex: number = state.items.findIndex(i => i._id === action.itemId);

      const oldFlags = state.items[itemToChangeIndex].flags;
      const newFlags = action.flags;

      if (isEqual(oldFlags, newFlags)) {
        return state;
      }

      return {
        ...state,
        items: state.items.map((item, index) => (index === itemToChangeIndex ? { ...item, flags: action.flags } : item)),
      };
    }
    case ProposalV2ActionTypes.ToggleGifHideBaseScenario: {
      return {
        ...state,
        gifHideBaseScenario: action.value,
      };
    }
    case ProposalV2ActionTypes.SetTemplate: {
      return {
        ...state,
        selectedTemplate: action.template,
      };
    }
    case ProposalV2ActionTypes.SetProposalTemplateOptions: {
      return {
        ...state,
        proposalTemplateOptions: action.proposalTemplateOptions,
      };
    }
    case ProposalV2ActionTypes.SetRiskProfile: {
      return {
        ...state,
        riskProfile: action.riskProfile,
      };
    }
    case ProposalV2ActionTypes.SetProposalContact: {
      return {
        ...state,
        contact: action.contact,
      };
    }
    case ProposalV2ActionTypes.SetProposalDate: {
      return {
        ...state,
        date: action.date,
      };
    }
    case ProposalV2ActionTypes.SetToolbarOption: {
      if (state.toolbarOptions[action.toolbarKey] !== undefined) {
        const oldOptions = state.toolbarOptions[action.toolbarKey];
        const newOptions = action.toolbarOption;

        if (isEqual(oldOptions, newOptions)) {
          return {
            ...state,
          };
        }
      }

      return {
        ...state,
        toolbarOptions: {
          ...state.toolbarOptions,
          [action.toolbarKey]: {
            ...(state.toolbarOptions && state.toolbarOptions[action.toolbarKey]),
            ...action.toolbarOption,
          },
        },
      };
    }
    case ProposalV2ActionTypes.SetProposalTemplates: {
      return {
        ...state,
        templates: action.templates,
      };
    }
    case ProposalV2ActionTypes.DownloadPDF: {
      return {
        ...state,
        downloadRequest: action.request,
      };
    }
    case ProposalV2ActionTypes.CancelPDFDownload: {
      return {
        ...state,
        downloadRequest: null,
      };
    }

    case ProposalV2ActionTypes.SetDefaultTemplate: {
      return {
        ...state,
        defaultTemplate: action.defaultTemplate,
      };
    }
    case ProposalV2ActionTypes.ChangeProposalItemsOrder: {
      const { itemIds } = action;

      return {
        ...state,
        items: itemIds.map((id: string) => state.items.find(i => i._id === id)) as ComparisonItem[],
      };
    }
    case ProposalV2ActionTypes.ChangeProposalContentOrder: {
      const { sections } = action;

      return {
        ...state,
        proposalTemplateOptions: {
          ...state.proposalTemplateOptions,
          sections,
        },
      };
    }
    default: {
      return state;
    }
  }
}

// Action Creators
export const createOmniPresentation =
  (comparisonId: string, contactId?: string): AsyncResult<{ url: string }> =>
  () => {
    const body: any = {
      comparisonId,
      contactId,
    };

    return api.post({ type: ProposalV2ActionTypes.CreateOmniPresentation, endpoint: 'proposals/omni' }, null, body);
  };

export const resetProposal = (): Result<void> => dispatch => {
  dispatch({ type: ProposalV2ActionTypes.ResetProposal } as ResetProposalAction);
};

// Looking for refreshProposalItems? It was moved to ./proposal-v2/actions.ts to prevent a troublesome circular dependency
// Refactor incoming.
// export const refreshProposalItems = (newItems: ComparisonItem[]): Result<void> => {}

export const setProposalItems =
  (items: ComparisonItem[]): Result<void> =>
  async (dispatch, getState) => {
    const {
      proposalV2: { selectedTemplate },
    } = getState();

    if (!selectedTemplate || selectedTemplate.builderVersion !== ProposalNS.ProposalBuilderVersion.V2) {
      return;
    }

    dispatch({ type: ProposalV2ActionTypes.SetItems, items: overrideItemColors(selectedTemplate, items) } as SetItemsAction);
  };

export const setProposalItemsCorrelation =
  (itemsCorrelation: CorrelationMetric): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.SetItemsCorrelation, itemsCorrelation } as SetItemsCorrelationAction);
  };

export const setProposalGifItems =
  (items: BMOGIFItem[]): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.SetGifItems, items } as SetGifItemsAction);
  };

export const setProposalItemFlags =
  (itemId: string, flags: string[]): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.SetProposalItemFlags, itemId, flags } as SetProposalItemFlagsAction);
  };

export const toggleGifHideBaseScenario =
  (value: boolean): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.ToggleGifHideBaseScenario, value } as ToggleGifHideBaseScenarioAction);
  };

export const setProposalTemplateOptions =
  (templateKey: string, proposalTemplateOptions: ProposalTemplateOptions): Result<void> =>
  dispatch => {
    localStorageUtils.setProposalV2Options(templateKey, proposalTemplateOptions);

    dispatch({ type: ProposalV2ActionTypes.SetProposalTemplateOptions, proposalTemplateOptions } as SetProposalTemplateOptionsAction);
  };

export const setDefaultTemplate =
  (defaultTemplate: string): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.SetDefaultTemplate, defaultTemplate });
    localStorageUtils.setDefaultProposalTemplate(defaultTemplate);
  };

export const setProposalTemplate =
  (template: ProposalNS.ProposalTemplateV2, setDefault?: boolean): Result<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const { comparison } = state;
    const currentComparison = getCurrentStateComparison(comparison);

    if (setDefault) {
      dispatch(setDefaultTemplate(template.key));
    }

    dispatch({ type: ProposalV2ActionTypes.SetTemplate, template } as SetTemplateAction);

    // Update the template locale option if the active comparison has a proposal locale value
    // This is needed to ensure that the proposal template language is set correctly
    if (currentComparison?.proposalLocale) {
      const proposalTemplateOptions = { ...template.options, locale: currentComparison.proposalLocale };
      localStorageUtils.setProposalV2Options(template.key, proposalTemplateOptions);
      dispatch({ type: ProposalV2ActionTypes.SetProposalTemplateOptions, proposalTemplateOptions });
    }
  };

export const setProposalTemplateHiddenLayouts =
  (templateKey: string, hiddenLayouts: string[]): Result<void> =>
  dispatch => {
    const proposalTemplateOptions = localStorageUtils.getProposalV2Options(templateKey);
    const newOptions = { ...proposalTemplateOptions, hiddenLayouts } as ProposalTemplateOptions;

    dispatch(setProposalTemplateOptions(templateKey, newOptions));
  };

export const setProposalTemplateSections =
  (templateKey: string, sections: string[]): Result<void> =>
  dispatch => {
    const proposalTemplateOptions = localStorageUtils.getProposalV2Options(templateKey);
    const newOptions = { ...proposalTemplateOptions, sections } as ProposalTemplateOptions;

    dispatch(setProposalTemplateOptions(templateKey, newOptions));
  };

export const setProposalTemplateDivisions =
  (templateKey: string, divisions: string[]): Result<void> =>
  dispatch => {
    const proposalTemplateOptions = localStorageUtils.getProposalV2Options(templateKey);
    const newOptions = { ...proposalTemplateOptions, divisions } as ProposalTemplateOptions;

    dispatch(setProposalTemplateOptions(templateKey, newOptions));
  };

export const setProposalTemplateLocale =
  (templateKey: string, locale: Locale): Result<void> =>
  dispatch => {
    const proposalTemplateOptions = localStorageUtils.getProposalV2Options(templateKey);
    const newOptions = { ...proposalTemplateOptions, locale } as ProposalTemplateOptions;

    dispatch(setProposalTemplateOptions(templateKey, newOptions));
  };

export const setProposalTemplateTheme =
  (templateKey: string, theme: string): Result<void> =>
  dispatch => {
    const proposalTemplateOptions = localStorageUtils.getProposalV2Options(templateKey);
    const newOptions = { ...proposalTemplateOptions, theme } as ProposalTemplateOptions;

    dispatch(setProposalTemplateOptions(templateKey, newOptions));
  };

export const setProposalStyleOptions =
  (templateKey: string, styleOptions: ProposalNS.ProposalStyleOptionsV2): Result<void> =>
  dispatch => {
    const proposalTemplateOptions = localStorageUtils.getProposalV2Options(templateKey);
    const newOptions = { ...proposalTemplateOptions, styleOptions } as ProposalTemplateOptions;

    dispatch(setProposalTemplateOptions(templateKey, newOptions));
  };

export const setProposalRiskProfile =
  (riskProfile: RiskProfileItem): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.SetRiskProfile, riskProfile } as SetRiskProfileAction);
  };

export const setToolbarOption =
  (toolbarKey: string, toolbarOption: ProposalToolbarOption, comparisonToolbarKey?: ComparisonToolbarKeys): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.SetToolbarOption, toolbarKey, toolbarOption } as SetToolbarOptionAction);

    if (comparisonToolbarKey) {
      switch (comparisonToolbarKey) {
        case ComparisonToolbarKeys.GROWTH:
        case ComparisonToolbarKeys.FUND_GROWTH:
        case ComparisonToolbarKeys.PORTFOLIO_GROWTH:
        case ComparisonToolbarKeys.GROUP_GROWTH:
        case ComparisonToolbarKeys.DRAWDOWN:
        case ComparisonToolbarKeys.INCOME: {
          dispatch(setChartOptions({ ...toolbarOption }, comparisonToolbarKey.toString() as PERFORMANCE_CHART));
          break;
        }
        case ComparisonToolbarKeys.RISK_REWARD: {
          if (toolbarOption.monthsRange) {
            dispatch(setChartOptions({ monthsRange: toolbarOption.monthsRange }, comparisonToolbarKey.toString() as PERFORMANCE_CHART));
          }
          break;
        }
        case ComparisonToolbarKeys.CALENDAR_YEAR_RETURNS: {
          if (toolbarOption.monthsRange) {
            dispatch(setCalendarYearPeriod(toolbarOption.monthsRange));
          }
          break;
        }
        case ComparisonToolbarKeys.FUND_CALENDAR_YEAR_PERIOD: {
          if (toolbarOption.monthsRange) {
            dispatch(setFundCalendarYearPeriod(toolbarOption.monthsRange));
          }
          break;
        }
        case ComparisonToolbarKeys.PORTFOLIO_CALENDAR_YEAR_PERIOD: {
          if (toolbarOption.monthsRange) {
            dispatch(setPortfolioCalendarYearPeriod(toolbarOption.monthsRange));
          }
          break;
        }
        case ComparisonToolbarKeys.GROUP_CALENDAR_YEAR_PERIOD: {
          if (toolbarOption.monthsRange) {
            dispatch(setGroupCalendarYearPeriod(toolbarOption.monthsRange));
          }
          break;
        }
        case ComparisonToolbarKeys.FUND_RISK_PERIOD: {
          if (toolbarOption.monthsRange !== undefined) {
            dispatch(setFundRiskPeriod(toolbarOption.monthsRange));
          }
          break;
        }
        case ComparisonToolbarKeys.PORTFOLIO_RISK_PERIOD: {
          if (toolbarOption.monthsRange !== undefined) {
            dispatch(setPortfolioRiskPeriod(toolbarOption.monthsRange));
          }
          break;
        }
        case ComparisonToolbarKeys.GROUP_RISK_PERIOD: {
          if (toolbarOption.monthsRange !== undefined) {
            dispatch(setGroupRiskPeriod(toolbarOption.monthsRange));
          }
          break;
        }
        case ComparisonToolbarKeys.RISK_METRICS:
        case ComparisonToolbarKeys.ADVANCED_RISK_METRICS: {
          if (toolbarOption.monthsRange !== undefined) {
            dispatch(setRiskPeriod(toolbarOption.monthsRange));
          }
          break;
        }

        case ComparisonToolbarKeys.CORRELATION_MATRICES:
        case ComparisonToolbarKeys.COMPARISON_CORRELATION_MATRIX: {
          // we currently do not persist this.
          break;
        }
      }
    }
  };

export const setTextAreaData =
  (key: string, textAreaData: ProposalTextAreaData): Result<void> =>
  (dispatch, getState) => {
    switch (key) {
      case ProposalNS.TEXT_AREA_KEYS.COMMENTARY: {
        if (textAreaData.value !== undefined) {
          dispatch(setComment(textAreaData.value));
        }
        break;
      }
      case ProposalNS.TEXT_AREA_KEYS.ABOUT: {
        if (textAreaData.value !== undefined) {
          const { proposalTemplateOptions, selectedTemplate } = getState().proposalV2;

          if (!selectedTemplate) {
            return;
          }

          const { locale } = proposalTemplateOptions;
          let { comments } = proposalTemplateOptions;

          if (comments === undefined) {
            comments = {};
          }

          let commentEntry: LocalizedString = comments[key];
          if (commentEntry === undefined) {
            comments[key] = { 'en-CA': undefined, 'fr-CA': undefined };
            commentEntry = comments[key];
          }

          comments[key][locale] = textAreaData.value;

          const newOptions = { ...proposalTemplateOptions, comments } as ProposalTemplateOptions;
          dispatch(setProposalTemplateOptions(selectedTemplate.key, newOptions));
        }
        break;
      }
    }
  };

export const setSelectedView =
  (templateKey: string, key: string, value: string): Result<void> =>
  dispatch => {
    const proposalTemplateOptions = localStorageUtils.getProposalV2Options(templateKey);
    const views: Record<string, string> = { ...proposalTemplateOptions?.views };
    views[key] = value;
    const newOptions = { ...proposalTemplateOptions, views } as ProposalTemplateOptions;

    dispatch(setProposalTemplateOptions(templateKey, newOptions));
  };

export const setProposalContact =
  (contact: ProposalNS.ProposalContact): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.SetProposalContact, contact } as SetProposalContactAction);
  };

export const setProposalDate =
  (date: Date): Result<void> =>
  dispatch => {
    dispatch({ type: ProposalV2ActionTypes.SetProposalDate, date } as SetProposalDateAction);
  };

export const loadProposalTemplates = (): AsyncResult<ProposalNS.ProposalTemplate[]> => (dispatch, getState) => {
  const action: GetProposalTemplatesAction = {
    type: ProposalV2ActionTypes.GetProposalTemplates,
    endpoint: 'proposals/templates',
  };

  return api.get(action).then((res: { data: any }) => {
    const templates = res.data as ProposalNS.ProposalTemplateV2[];
    dispatch({ type: ProposalV2ActionTypes.SetProposalTemplates, templates } as SetProposalTemplatesAction);

    // Find default template from saved options - if no default template is found, use the first template available.
    const defaultTemplate = localStorageUtils.getDefaultProposalTemplate();

    let proposalTemplate = defaultTemplate != null && templates.find(template => template.key === defaultTemplate);
    if (proposalTemplate) {
      dispatch(setDefaultTemplate(proposalTemplate.key));
    } else {
      proposalTemplate = templates[0];
    }

    dispatch(setProposalTemplate(proposalTemplate));

    return templates;
  });
};

export const downloadProposalPDF =
  (title: string, brandingInfo: Nullable<Dictionary<string | undefined>>, proposalPdfMetadata?: ProposalPdfMetadata): AsyncResult<ApiResponse<Blob | null>> =>
  async (dispatch, getState) => {
    const state = getState();
    const { comparison, auth } = state;
    const proposalV2 = state.proposalV2 as ProposalV2State;
    const currentComparison = getCurrentStateComparison(comparison);

    if (!currentComparison) {
      return Promise.reject(new Error('No current comparison - cannot generate proposal.'));
    }

    let doc = window.document.querySelector('.proposal-preview-component');

    if (!doc) {
      return Promise.reject(new Error('Could not parse the proposal document'));
    }

    doc = doc.cloneNode(true) as Element;
    doc.classList.add('print');

    const body: any = {
      content: doc.outerHTML,
      metadata: {
        comparisonName: currentComparison.name,
        bodyClass: 'proposal-v2',
        locale: proposalV2.proposalTemplateOptions.locale,
        template: proposalV2.selectedTemplate && proposalV2.selectedTemplate.key,
        author: proposalPdfMetadata?.author || '',
        keywords: proposalPdfMetadata?.keywords || [],
        subject: proposalPdfMetadata?.subject || '',
        title: proposalPdfMetadata?.title || title,
        contactId: proposalV2.contact?._id,
        householdName: currentComparison.householdName,
        householdId: currentComparison.householdId,
        missingHoldingsProxyLog: getMissingHoldingsProxyLog(currentComparison.items),
        comparisonId: currentComparison._id,
        nameId: auth?.user?.nameId,
      },
      riskProfile: proposalV2.riskProfile,
      items: proposalV2.items.map(item => ({
        _id: item._id,
        type: item.type,
        portfolioType: item.portfolioType,
        riskProfile: item.riskProfile,
        contactId: item.contact,
      })),
    };

    const template = proposalV2.selectedTemplate as ProposalNS.ProposalTemplateV2 | undefined;

    if (template && template.pageFormat) {
      // If template has a page format set - use that over any other config.
      body.metadata.pageFormat = template.pageFormat;
    } else {
      // Otherwise, use the format and landscape flag from the template options.
      body.metadata.format = proposalV2.proposalTemplateOptions.styleOptions.paperFormat;
      body.metadata.landscape = proposalV2.proposalTemplateOptions.styleOptions.orientation === PROPOSAL_ORIENTATION.LANDSCAPE;
    }

    // If comparison is an existing comparison, send the comparison id inside payload - mostly needed for logs.
    if (currentComparison._id && currentComparison._id.indexOf('new-') === -1) {
      body.metadata.comparisonId = currentComparison._id;
    }

    const { sections } = proposalV2.proposalTemplateOptions;
    const metadata = {
      ...body.metadata,
      advisor: brandingInfo,
    };

    // Apply same logic for the title that will be logged as in the title that gets sent to the CRM API externally
    const proposalName = proposalPdfMetadata?.title?.[proposalV2.proposalTemplateOptions.locale] || title;
    logDownloadProposal({ items: proposalV2.items, sections, metadata, riskProfileUpRisk: proposalV2?.riskProfile?.upRisk, proposalName });

    return api.post({ type: ProposalV2ActionTypes.DownloadPDF, endpoint: 'proposals/download' }, null, body, {
      responseType: 'blob',
      returnFullResponse: true,
    });
  };

export const cancelPDFDownload = (): Result<void> => (dispatch, getState) => {
  const { downloadRequest } = getState().proposalV2;

  if (downloadRequest) {
    downloadRequest.cancel();
  }

  dispatch({ type: ProposalV2ActionTypes.CancelPDFDownload } as CancelPDFDownloadAction);
};

export const changeProposalItemsOrder =
  (itemIds: string[]): Result<void> =>
  (dispatch, getState) => {
    dispatch({ type: ProposalV2ActionTypes.ChangeProposalItemsOrder, itemIds });
  };

export const changeProposalContentOrder =
  (sections: string[]): Result<void> =>
  (dispatch, getState) => {
    dispatch({ type: ProposalV2ActionTypes.ChangeProposalContentOrder, sections });
  };
